import React from 'react';
import HBSideDrawer, { IHBSideDrawer } from '../HBSideDrawer/HBSideDrawer';
import { IconComponent } from '@honeybook/icons';
import { HBBadgeNewProps } from '../HBBadge/HBBadgeNew';
import { NumericBadgeProps } from '../HBNumericBadge/HBNumericBadge';
import { IMenuItemContainer } from './SideNavMenuItem/SideNavMenuItemContainer';
import SideNavContent, {
    ItemOpenChangeDetails,
    PostContentRendererProps,
    PreContentRendererProps
} from './SideNavContent/SideNavContent';
import { ISideNavHeaderProps } from './SideNavHeader/SideNavHeader';
import SideNavProvider from './context/SideNavProvider';
import { ISideNavTheme, ISideNavThemeProps } from './context/useCreateTheme';

interface IBaseSideNavItem {
    icon?: IconComponent;
    /** Renders a custom icon component instead of the standard IconComponent; takes priority over icon. */
    customIcon?: () => JSX.Element;
    label: string;
    description?: string;
    /** HTML anchor attributes applied when the menu item renders as a link element. */
    linkProps?: Partial<Pick<HTMLAnchorElement, 'href' | 'target' | 'rel'>>;
    /** Click handler receiving the event and details including the item, parent label, and whether the click originated from the sidenav or a collapsed-state popover. */
    onClick?: (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        details: {
            item: ISideNavItem;
            parentLabel?: ISideNavItem['label'];
            source?: 'sidenav' | 'popover';
            /** Set to 'auto' when hbui itself invokes the handler (navigating to the first sub-item as a group expands) rather than the user clicking the item. */
            trigger?: 'auto';
        }
    ) => void;
    /** Nested navigation items rendered as an expandable section with a chevron indicator. */
    subItems?: ISideNavItem[];
    /** Custom popover content shown when the sidenav is collapsed and the item is clicked; receives theme and click handler for full control. */
    renderPopover?: ({
        theme,
        menuItem,
        onMenuItemClick,
        source
    }: {
        theme?: ISideNavTheme;
        menuItem: ISideNavItem;
        onMenuItemClick: IMenuItemContainer['onMenuItemClick'];
        source?: 'sidenav' | 'popover';
    }) => JSX.Element;
    isSelected?: boolean;
    /** Label of the parent item when this item is nested; used for click handler context. */
    parentLabel?: string;
    /** When true, clicking this item does not collapse the sidenav. */
    preventCloseOnClick?: boolean;
    /** URL for external navigation; renders an external link indicator. */
    externalUrl?: string;
    testIdName?: string;
    /** When true, clicking this item toggles its expanded state instead of navigating. */
    openOnClick?: boolean;
}

/** Discriminated union: when badgeType is 'numeric', badgeProps is required with NumericBadgeProps; when 'new' or omitted, badgeProps is optional. */
export type ISideNavItem =
    | (IBaseSideNavItem & {
          badgeType?: 'new';
          badgeProps?: HBBadgeNewProps;
      })
    | (IBaseSideNavItem & {
          badgeType: 'numeric';
          badgeProps: NumericBadgeProps;
      });

export interface HBSideNavProps {
    /** HTML data-testid applied to the root sidenav element. */
    dataTestId?: string;
    /** Whether the sidenav is expanded. Omit to let the component manage open state internally. */
    open?: boolean;
    /** Primary navigation items rendered in the main scrollable area. */
    menuItems: ISideNavItem[];
    /** Navigation items pinned to the bottom of the sidenav, below the main menu. */
    footerMenuItems: ISideNavItem[];
    /** Menu items passed to the preContentRenderer for rendering above the main navigation. */
    preContentRendererMenuItems?: ISideNavItem[];
    /** Custom theme overrides merged with the default sidenav color tokens. */
    theme?: ISideNavThemeProps;
    /** Whether the sidenav starts in pinned (always expanded) state. */
    initialPinned: boolean;
    /** Width in pixels when the sidenav is fully expanded. */
    expandedWidth: number;
    /** Width in pixels when the sidenav is collapsed to icon-only mode. */
    collapsedWidth: number;
    /** Label of the currently active item; used to highlight the selected menu item. */
    activeItem?: string;
    /** When true, disables the popover menus that appear when clicking items in collapsed state. */
    supressPopovers?: boolean;
    /** Replaces the default header with a custom renderer. */
    customHeaderRenderer?: () => JSX.Element;
    /** Renders custom content above the main menu; receives the resolved theme. */
    preContentRenderer?: ({ theme }: PreContentRendererProps) => JSX.Element;
    /** Renders custom content below the main menu; receives the resolved theme. */
    postContentRenderer?: ({
        theme
    }: PostContentRendererProps) => React.ReactNode;
    /** Renders a title element above the footer menu items. */
    footerTitle?: () => JSX.Element;
    /** Called when the sidenav opens or closes. */
    onOpenChange?: (isOpen: boolean) => void;
    /** Called when a group opens or closes, with whether the user clicked ('click') or the active route changed ('route'). Opening one group while another is open reports the close first. */
    onItemOpenChange?: (details: ItemOpenChangeDetails) => void;
    /** Called when the user pins or unpins the sidenav from its expanded state. */
    onPinChange?: IHBSideDrawer['onPinChange'];
    /** Props forwarded to the header: `logoHref` (default '/'), `autoCollapseWidth` (viewport width below which the pin button hides and the nav force-collapses), `onLogoClick`, and `hidePinButton`. */
    headerProps?: ISideNavHeaderProps;
}

function HBSideNav({
    dataTestId,
    open,
    menuItems,
    footerMenuItems,
    preContentRendererMenuItems,
    theme,
    initialPinned,
    collapsedWidth,
    expandedWidth,
    headerProps,
    activeItem,
    supressPopovers,
    customHeaderRenderer,
    preContentRenderer,
    postContentRenderer,
    footerTitle,
    onOpenChange,
    onItemOpenChange,
    onPinChange
}: HBSideNavProps) {
    return (
        <HBSideDrawer
            data-testid={dataTestId}
            open={open}
            collapsedWidth={collapsedWidth}
            expandedWidth={expandedWidth}
            initialPinned={initialPinned}
            onOpenChange={onOpenChange}
            onPinChange={onPinChange}
        >
            <SideNavProvider
                context={{
                    theme,
                    supressPopovers
                }}
            >
                <SideNavContent
                    customHeaderRenderer={customHeaderRenderer}
                    preContentRenderer={preContentRenderer}
                    menuItems={menuItems}
                    footerMenuItems={footerMenuItems}
                    footerTitle={footerTitle}
                    headerProps={headerProps}
                    activeItem={activeItem}
                    preContentRendererMenuItems={preContentRendererMenuItems}
                    postContentRenderer={postContentRenderer}
                    onItemOpenChange={onItemOpenChange}
                />
            </SideNavProvider>
        </HBSideDrawer>
    );
}

export default HBSideNav;
